import { Share2, Link2, Mail, MessageSquare, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { divisionOf, formatPrice } from "../../lib/api";

export const isShareable = (a) => Boolean(a?.verified && a?.published !== false && a?.status !== "sold");

const listingUrl = (a) => `${window.location.origin}/api/listing/${encodeURIComponent(a.id)}`;

const shorten = (t, n = 160) => {
  const s = (t || "").replace(/\s+/g, " ").trim();
  return s.length <= n ? s : `${s.slice(0, s.lastIndexOf(" ", n) > 80 ? s.lastIndexOf(" ", n) : n).trim()}…`;
};

export const shareText = (a) => {
  const ymm = [a.year, a.make, a.model].filter(Boolean).join(" ");
  return [a.title, [ymm, divisionOf(a.division)?.name].filter(Boolean).join(" · "), formatPrice(a.price), shorten(a.description)]
    .filter(Boolean).join("\n");
};

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    el.remove();
    return ok;
  }
};

const Item = ({ icon: Icon, label, onSelect, testid }) => (
  <DropdownMenuItem onSelect={onSelect} className="gap-3 py-2.5 cursor-pointer text-slate-100 focus:bg-white/10 focus:text-slate-50" data-testid={testid}>
    <Icon className="w-4 h-4 text-gold" /> {label}
  </DropdownMenuItem>
);

export const ShareListing = ({ asset, compact = false, className = "" }) => {
  if (!isShareable(asset)) return null;
  const url = listingUrl(asset);
  const text = shareText(asset);
  const subject = `${asset.title} | Selby Asset Group`;
  const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const id = asset.id;

  const onCopy = async () => {
    if (await copy(url)) toast.success("Listing link copied", { description: "Paste it anywhere to share this listing." });
    else toast.error("Couldn't copy automatically. Copy the address from your browser bar.");
  };
  const onEmail = () => {
    const body = `Hi,\r\n\r\nI thought you might be interested in this listing from Selby Asset Group:\r\n\r\n${text.replace(/\n/g, "\r\n")}\r\n\r\nView listing: ${url}\r\n`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  const onText = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(`${text}\n${url}`)}`;
  };
  const onNative = async () => {
    try {
      await navigator.share({ title: subject, text, url });
    } catch (e) {
      if (e?.name !== "AbortError") onCopy();
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <button type="button" aria-label={`Share ${asset.title}`} onClick={(e) => e.stopPropagation()}
            className={`w-10 h-10 grid place-items-center bg-[#0a0d14]/70 backdrop-blur border border-white/25 text-slate-100 hover:border-[var(--gold)] hover:text-gold transition-colors ${className}`}
            data-testid={`share-listing-button-${id}`}>
            <Share2 className="w-4 h-4" />
          </button>
        ) : (
          <button type="button" className={`btn-ghost w-full !py-3 ${className}`} data-testid="share-listing-button"><Share2 className="w-4 h-4" /> Share Listing</button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-60 rounded-none bg-[var(--ink-2)] border-white/15 text-slate-100 p-1" data-testid="share-listing-menu">
        {canNative && <Item icon={Smartphone} label="Share via device…" onSelect={onNative} testid="share-listing-native" />}
        <Item icon={Link2} label="Copy Listing Link" onSelect={onCopy} testid="share-listing-copy" />
        <Item icon={Mail} label="Share by Email" onSelect={onEmail} testid="share-listing-email" />
        <Item icon={MessageSquare} label="Share by Text Message" onSelect={onText} testid="share-listing-sms" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
